import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserType } from '../../enums';
import * as userActions from '../../store/actions/user';
import ErrorMessage from '../../components/ErrorMessage';
import { getNextStepPath, getOnboardingStepPath, getPreviousStepPath, isKycOrLaterOnboardingStep } from '../../utils/onboardingUtils';
import { Building2Icon, UserIcon } from '../../assets/icons';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';

function ChooseUserType() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, onboardingStatus, settingOnboardingUserType, setOnboardingUserTypeError } = useSelector((state) => state.user);
  const [userType, setUserType] = useState('');
  const hasJustContinuedRef = useRef(false);
  const setFooterConfig = useOnboardingFooter();
  const nextStepPath = getNextStepPath(location.pathname, onboardingStatus?.steps);
  const previousStepPath = getPreviousStepPath(location.pathname, onboardingStatus?.steps);
  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);
  const isSelectionLocked = isKycOrLaterStep || settingOnboardingUserType || Boolean(userType);

  useEffect(() => {
    if (
      hasJustContinuedRef.current &&
      !settingOnboardingUserType &&
      !setOnboardingUserTypeError &&
      onboardingStatus?.currentStep &&
      onboardingStatus.currentStep !== 'ChooseUserType'
    ) {
      const nextPath = getOnboardingStepPath(onboardingStatus.currentStep);
      if (nextPath) {
        hasJustContinuedRef.current = false;
        navigate(nextPath, { replace: true });
      }
    }
  }, [settingOnboardingUserType, setOnboardingUserTypeError, onboardingStatus?.currentStep, navigate]);

  useEffect(() => {
    if (user?.userType) {
      setUserType(user.userType);
    }
  }, [user?.userType]);

  const handleSelect = (type) => {
    if (isSelectionLocked) return;
    setUserType(type);
  };

  const handleContinue = useCallback(() => {
    if (!userType || !user) return;
    if (isKycOrLaterStep) {
      if (nextStepPath) navigate(nextStepPath);
      return;
    }
    const isCurrentStep = onboardingStatus?.currentStep === 'ChooseUserType';
    if (isCurrentStep) {
      hasJustContinuedRef.current = true;
      dispatch(
        userActions.setOnboardingUserType({
          ...user,
          userType,
        }),
      );
    } else if (nextStepPath) {
      navigate(nextStepPath);
    }
  }, [dispatch, isKycOrLaterStep, navigate, nextStepPath, onboardingStatus?.currentStep, user, userType]);

  const handleBack = useCallback(() => {
    if (previousStepPath) {
      navigate(previousStepPath);
    }
  }, [navigate, previousStepPath]);

  let continueButtonLabel = 'Continue';
  if (settingOnboardingUserType) {
    continueButtonLabel = 'Saving...';
  }

  useEffect(() => {
    setFooterConfig({
      continueLabel: continueButtonLabel,
      continueDisabled: !userType || settingOnboardingUserType,
      onContinue: handleContinue,
      backDisabled: !previousStepPath || settingOnboardingUserType,
      onBack: handleBack,
    });

    return () => setFooterConfig({});
  }, [continueButtonLabel, handleBack, handleContinue, previousStepPath, setFooterConfig, settingOnboardingUserType, userType]);

  if (!user) {
    return null;
  }

  const accountOptions = [
    {
      type: UserType.Individual,
      title: 'Individual',
      description: 'For personal use and transfers',
      icon: <UserIcon className='h-6 w-6' />,
    },
    {
      type: UserType.Company,
      title: 'Company',
      description: 'For businesses and organisations',
      icon: <Building2Icon className='h-6 w-6' strokeWidth='1.75' />,
    },
  ];

  return (
    <div className='flex justify-center px-4 pb-24 pt-9 sm:px-6'>
      <div className='w-full max-w-[540px] rounded-[20px] bg-white px-6 py-9 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
        <div>
          <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
            <UserIcon className='h-6 w-6' />
          </div>

          <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>
            What kind of account are you opening{user?.firstName ? `, ${user.firstName}` : ''}?
          </h1>
          <p className='mb-5 mt-2 text-sm leading-[1.55] text-[#7A9AB0]'>Choose the account type that fits your needs.</p>
        </div>

        <div className='space-y-4'>
          <div className='grid gap-2.5 sm:grid-cols-2'>
            {accountOptions.map((option) => {
              const selected = userType === option.type;

              return (
                <button
                  key={option.type}
                  type='button'
                  onClick={() => handleSelect(option.type)}
                  disabled={isSelectionLocked}
                  aria-pressed={selected}
                  className={`group relative flex min-h-[82px] items-start gap-3 rounded-2xl border-[1.5px] bg-white px-[18px] py-4 text-left transition focus:outline-none focus:ring-4 focus:ring-[#2E6BAD]/10 disabled:cursor-not-allowed disabled:opacity-60 ${
                    isSelectionLocked
                      ? ''
                      : 'hover:-translate-y-px hover:border-[#8DBDE6] hover:bg-[#EBF4FB] hover:shadow-[0_3px_12px_rgba(11,25,41,0.06)]'
                  } ${
                    selected ? 'border-[#1E4E80] bg-[#EBF4FB] shadow-[0_0_0_1px_#1E4E80]' : 'border-[#E2EBF3]'
                  }`}
                >
                  <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#163659]'>
                    {option.icon}
                  </span>
                  <span className='min-w-0'>
                    <span className='block text-sm font-semibold text-[#0B1929]'>{option.title}</span>
                    <span className='mt-[3px] block text-[12.5px] leading-[1.35] text-[#3D5A73]'>{option.description}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {setOnboardingUserTypeError && (
            <ErrorMessage error={setOnboardingUserTypeError?.message || setOnboardingUserTypeError?.detail || 'Failed to set user type.'} />
          )}
        </div>
      </div>
    </div>
  );
}

export default ChooseUserType;
